import { Component, DestroyRef, inject, input, Input, OnInit, Output, EventEmitter } from '@angular/core';
import {
  NbAccordionModule,
  NbButtonModule,
  NbCardModule,
  NbDialogRef,
  NbDialogService,
  NbFormFieldModule,
  NbInputModule,
  NbListModule,
  NbSelectModule,
  NbTooltipModule,
} from '@nebular/theme';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { BudgetForm, FormGroupFrom, IInvestmentWizard } from '../wizard-interface';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { I18nService, PriceSheetService, ScopeService } from '../../../../../@core/utils';
import { idMapToArray } from '../../../../../@core/utils/scope.service';
import * as R from 'ramda';
import { combineLatest, forkJoin, Observable, of } from 'rxjs';
import { distinctUntilChanged, filter, first, map, switchMap, tap } from 'rxjs/operators';
import { InvestmentWizardService } from '../../../../../@core/utils/investment-wizard-service';
import { KPI } from '../../../../../@core/api';
import { AssetTypesService } from '../../../../../@core/utils/asset-types.service';
import { PriceSheetsListComponent } from '../../../price-sheets-list/price-sheets-list.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { IntegerOnlyDirective } from '../../../../../@core/directives';
import { NewAssetWizardState } from './wizard-new-asset-step.component';
import { PriceTier } from '../../../../../@core/utils/auto-ppa.service';

@Component({
  selector: 'app-price-sheet-wrapper',
  standalone: true,
  imports: [
    NbCardModule,
    PriceSheetsListComponent,
    NbButtonModule,
    TranslateModule,
  ],
  template: `
    <nb-card style="min-width: 75vw; max-width: 75vw; max-height: 100vh">
      <nb-card-header>
        <div class="fs-4 pb-2">
          {{ "Price sheets" | translate }}
        </div>
        <div class="">
          <span style="color: #9e9e9e; font-size: 14px; font-weight: 400;">{{ createBreadCrumb() }} ></span> <span style="color: #c3351a; font-size: 14px"> {{ assetType.name }}</span>
        </div>
      </nb-card-header>
      <nb-card-body>
        <app-price-sheets-list
          [priceSheets]="priceSheets"
          [assetType]="assetType"
          [currentAssetId]="currentAssetId"
          [isPPA]="isPPA"
          (priceSheetSelected)="onPriceSheetSelected($event)"
          [automaticCheckDefaultPriceSheet]="false"
          [readOnly]="readOnly"
          [inWizard]="inWizard"
          [selectedPriceSheetId]="selectedPriceSheetId"
          (priceTierChanged)="priceTierChanged($event)"
        ></app-price-sheets-list>
      </nb-card-body>
      <nb-card-footer>
        @if (!readOnly) {
          <button nbButton (click)="transmitBack()">{{ 'Apply the pricesheet' | translate }}</button>
        }
      </nb-card-footer>
    </nb-card>
  `,
  styleUrls: [
    '../wizard-index.component.scss',
  ],
})
export class PriceSheetWrapperComponent {
  @Input({ required: true }) priceSheets: KPI.PriceSheet[];
  @Input({ required: true }) assetType: KPI.MappedAssetType;
  @Input({ required: true }) currentAssetId: number;
  @Input({ required: true }) showIsDefault: boolean;
  @Input({ required: true }) isPPA: boolean;
  @Input({ required: false }) selectedPriceSheetId?: number;
  @Input({ required: false }) readOnly: boolean;
  @Input({ required: false }) inWizard: boolean;

  dialogRef = inject(NbDialogRef);

  // store the state before transmit to the parent
  private selectedPriceSheet: KPI.PriceSheet;
  private selectedPriceTier: PriceTier = null;

  public createBreadCrumb(): string {
    return this.assetType.super_category.name + ' > ' + this.assetType.category.name + ' > ' + this.assetType.sub_category.name;
  }

  onPriceSheetSelected($event: KPI.PriceSheet) {
    this.selectedPriceSheet = $event;
  }

  priceTierChanged(priceTier: PriceTier) {
    this.selectedPriceTier = priceTier;
  }

  transmitBack() {
    this.dialogRef.close({
      selectedPriceSheet: this.selectedPriceSheet,
      priceSheetTier: this.selectedPriceTier
    });
  }
}

@Component({
  selector: 'app-wizard-budget-allocation',
  standalone: true,
  imports: [
    NbListModule,
    CommonModule,
    NbInputModule,
    NbFormFieldModule,
    TranslateModule,
    ReactiveFormsModule,
    NbButtonModule,
    NbSelectModule,
    NbAccordionModule,
    NbTooltipModule,
    IntegerOnlyDirective,
  ],
  template: `
    <div class="wrapper mt-2">
      <form [formGroup]="budgetForm" class="investment-form" autocomplete="off">
        <div class="form-group">
          <div class="form-field" [class.attention]="!readOnly() && !budgetForm.controls['price'].valid">
          <nb-form-field>
            <label for="price">{{ "Amount" | translate }} ({{ currency }})</label>
            <input nbInput id="price" type="number" formControlName="price" placeholder="-"
                   autocomplete="off" integerOnly/>
            <button nbSuffix nbButton ghost (click)="openPriceSheets()"
              class="price-sheet-icon {{ extraCss }}"
              [nbTooltip]="'No asset is selected' | translate" [nbTooltipDisabled]="!!selectedAssetState">
              <i class="fa-light fa-book-open-reader me-1"></i>
              ({{ (priceSheets$ | async)?.length }})
            </button>
          </nb-form-field>
          </div>
          <div class="form-field" [class.attention]="!readOnly() && !budgetForm.controls['final_schedule_to'].valid">
            <label for="final_schedule_to">{{ "Year" | translate }}</label>
            <nb-select formControlName="final_schedule_to" fullWidth>
              @for (year of years; track year) {
                <nb-option [value]="year">{{ year }}</nb-option>
              }
            </nb-select>
          </div>
          @if (showStatusFormControl()) {
            <div class="form-field">
              <label for="status">{{ "Status" | translate }}</label>
              <nb-select formControlName="status" fullWidth [compareWith]="compareById">
                @for (status of (allowedStatuses() ?? (investmentStatus$ | async)); track status) {
                  <nb-option [value]="status">{{ status.name }}</nb-option>
                }
              </nb-select>
            </div>
          }
          <div class="form-field">
            <label for="budget_origin">{{ "Budget origin" | translate }}</label>
            <nb-select formControlName="budget_origin" fullWidth [compareWith]="compareById">
              @for (budgetOrigin of budgetOrigins; track budgetOrigin.id) {
                <nb-option [value]="budgetOrigin">{{ budgetOrigin.name }}</nb-option>
              }
            </nb-select>
          </div>
          <div class="form-field" style="border-bottom: none;">
            <label for="budget_scopes">{{ "Budget scope" | translate }}</label>
            <nb-select formControlName="budget_scopes" fullWidth [compareWith]="compareById">
              @for (budgetScope of (budgetScopes$ | async); track budgetScope) {
                <nb-option [value]="budgetScope">{{ budgetScope.name }}</nb-option>
              }
            </nb-select>
          </div>

          <div class="mt-3"
            style="display: flex; justify-content: center; padding: 4px 0; background-color: #f0f0f0; color: #404040; font-size: 12px;">
            (+) Afficher le budget détaillé
          </div>
        </div>
      </form>
    </div>
  `,
  styleUrls: [
    '../wizard-index.component.scss',
    '../../../side-forms/base-form/base-form.component.scss',
  ],
})
export class BudgetAllocationComponent implements OnInit, IInvestmentWizard {
  readonly stateKey = 'BUDGET_ALLOCATION';
  state = () => this.wizardService.getState(this.stateKey);

  // a configuration to make the budget form readonly or not
  readOnly = input(false);
  // for editing existing investments, sometimes we don't want to show the status control
  // it's to disable changing the status with the logic of status transition
  showStatusFormControl = input(true);
  // filtered statuses to show (same logic as investment form outside); falls back to all statuses
  allowedStatuses = input<KPI.InvestmentStatus[] | null>(null);

  // investment type is mandatory. if it's not present, MAKE SURE TO INITIALIZE IT in the wizard service
  investmentTypeState!: KPI.InvestmentType;

  // available years for final schedule to
  years = R.range(new Date().getFullYear() - 1, new Date().getFullYear() + 60);
  // catalog of budget origins
  budgetOrigins!: KPI.InvestmentBudgetOrigin[];
  currency = '€';

  // dependencies
  private scope = inject(ScopeService);
  private wizardService = inject(InvestmentWizardService);
  private assetTypesService = inject(AssetTypesService);
  private priceSheetService = inject(PriceSheetService);
  private dialogService = inject(NbDialogService);
  private destroyRef = inject(DestroyRef);
  private i18n = inject(I18nService)

  // Hack for the priceSheet icon position, because I don't understand exactly
  // why the wizard budget step has a big padding on the right side. We have to ajust
  // the icon weither we are in the wizard or in the invest form.
  public extraCss: string = '';

  @Output() formChanged = new EventEmitter<void>();

  // in case the user does not select an asset from previous step
  // this variable will be null
  selectedAssetState?: Partial<KPI.Asset> = this.wizardService.getState('ASSET_SELECTION');
  newAssetState?: NewAssetWizardState = this.wizardService.getState('NEW_ASSET_STEP');

  // catalogs of objects
  budgetOrigins$ = this.scope.investmentBudgetOrigins$.pipe(first(), idMapToArray());
  budgetScopes$ = this.scope.budgetScopes$.pipe(first(), idMapToArray());
  investmentStatus$ = this.scope.investmentStatuses$.pipe(first(), idMapToArray());
  requestedStatus$ = this.investmentStatus$.pipe(
    map(statuses => statuses.filter(status => status.is_request))
  );
  invPriorities$ = this.scope.investmentPriorities$.pipe(first(), idMapToArray());
  priceSheets$: Observable<KPI.PriceSheet[]>;

  // main form
  budgetForm: FormGroupFrom<BudgetForm> = new FormGroup({
    price: new FormControl<number | null>(null, [Validators.required, Validators.min(0)]),
    status: new FormControl<KPI.InvestmentStatus | null>(null, Validators.required),
    final_schedule_to: new FormControl<number | null>(null, Validators.required),
    budget_origin: new FormControl<KPI.InvestmentBudgetOrigin | null>(null, Validators.required),
    budget_scopes: new FormControl<KPI.BudgetScope>(null),
    is_capex: new FormControl<boolean>(true),
    aligned_taxonomy: new FormControl<boolean>(true),
    priority: new FormControl<KPI.InvestmentPriority>(null),
    price_sheet_id: new FormControl<number>(null),
  });

  private initialValues: Partial<BudgetForm> = {};

  ngOnInit(): void {
    const state = this.state();
    this.currency = this.i18n.getCurrency();
    this.investmentTypeState = this.wizardService.getState('INVESTMENT_TYPE_SELECTION');
    if (!this.investmentTypeState) throw Error('No investment type selected. If you want, make sure to fill the state before accessing this component');
    const selectedAssetType = this.selectedAssetState?.asset_type;
    const newAssetType = this.newAssetState?.value.asset_type.id;

    this.setReadOnlyMode(this.readOnly());

    if (this.newAssetState) {
      this.priceSheets$ = this.priceSheetService.getPriceSheetsByAssetType(newAssetType).pipe(first());
    } else if (this.selectedAssetState && selectedAssetType) {
      this.priceSheets$ = this.priceSheetService.getPriceSheetsByAssetType(selectedAssetType).pipe(first());
    } else {
      this.priceSheets$ = of([]);
    }
    // control the form changes whether valid or not
    this.budgetForm.valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => {
      this.wizardService.setState('BUDGET_ALLOCATION', <BudgetForm>this.budgetForm.getRawValue());
      this.budgetForm.updateValueAndValidity({ emitEvent: false });

      if (this.budgetForm.valid) {
        this.wizardService.unblockCurrentStep();
      } else {
        this.wizardService.blockCurrentStep('Please fill all required fields to continue');
      }
      if (Object.keys(this.initialValues).length > 0) {
        this.formChanged.emit();
      }
    });

    this.budgetForm.get('budget_scopes').valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => {
      this.budgetForm.updateValueAndValidity({ emitEvent: false });
      this.formChanged.emit();
    });

    forkJoin([
      this.budgetOrigins$,
      this.budgetScopes$,
      this.investmentStatus$,
      this.invPriorities$
    ]).pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(
      ([budgetOrigins, budgetScopes, investmentStatus, priorities]) => {
        this.budgetOrigins = budgetOrigins
        if (!!state) {
          const statusToPatch = state.status
            ? (typeof state.status === 'object' && state.status.id
                ? investmentStatus.find(s => s.id === state.status.id) || state.status
                : state.status)
            : state.status;
          const budgetScopeToPatch = state.budget_scopes
            ? (typeof state.budget_scopes === 'object' && state.budget_scopes.id
                ? budgetScopes.find(bs => bs.id === state.budget_scopes.id) || state.budget_scopes
                : state.budget_scopes)
            : state.budget_scopes;
          const priorityToPatch = state.priority
            ? (typeof state.priority === 'object' && state.priority.id
                ? priorities.find(p => p.id === state.priority.id) || state.priority
                : state.priority)
            : state.priority;

          this.budgetForm.patchValue({
            ...state,
            status: statusToPatch,
            budget_scopes: budgetScopeToPatch,
            priority: priorityToPatch
          });

          this.initialValues = { ...this.budgetForm.getRawValue() };
        } else {
          this.budgetForm.patchValue({
            status: this.findDefault(investmentStatus),
            budget_origin: this.findDefault(budgetOrigins),
            budget_scopes: this.findDefault(budgetScopes),
            priority: this.findDefault(priorities),
          });
          if (this.selectedAssetState && this.selectedAssetState.end_of_life) {
            this.budgetForm.patchValue({
              final_schedule_to: this.selectedAssetState.end_of_life,
            });
          }
          this.initialValues = { ...this.budgetForm.getRawValue() };
        }
      },
    );
    this.budgetForm.get('price').valueChanges.pipe(
      distinctUntilChanged(),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => {
      // when price changes, we reset the budget origin
      this.budgetForm.patchValue({ budget_origin: this.findDefault(this.budgetOrigins) });
    });

    if (!this.wizardService.investmentId) {
      this.extraCss = 'extra-margin';
    }
  }

  findDefault = <T extends { is_default: boolean }>(lst: T[]) => {
    const dv = lst.find(item => item.is_default);
    if (!dv) throw new Error(`Could not find default in list ${lst}`);
    return dv;
  };

  compareById = (o1: { id: number }, o2: { id: number }) => {
    return o1 && o2 ? o1.id === o2.id : o1 === o2;
  }

  public openPriceSheets() {
    if ((!this.selectedAssetState || !this.selectedAssetState.asset_type) && !this.newAssetState) {
      console.debug('No asset selected, cannot open price sheets');
      return
    }
    const asset = this.selectedAssetState,
      assetTypeId = this.newAssetState ? this.newAssetState.value.asset_type.id : asset.asset_type,
      budgetOriginPriceSheet = this.budgetOrigins.find(bo => bo.price_list);
    return combineLatest([
      this.assetTypesService.mappedAssetTypes$,
      this.priceSheetService.getPriceSheetsByAssetType(assetTypeId),
    ]).pipe(
      first(),
      switchMap(([assetTypes, priceSheets]) => {
        const assetType = assetTypes.find((a) => a.id === assetTypeId);
        const dialogRef = this.dialogService.open(
          PriceSheetWrapperComponent,
          {
            autoFocus: false,
            context: {
              assetType,
              priceSheets,
              currentAssetId: asset?.id || null,
              showIsDefault: true,
              isPPA: false,
              selectedPriceSheetId: this.budgetForm.get('price_sheet_id').value,
              readOnly: this.readOnly(),
              inWizard: true
            },
          },
        );
        return dialogRef.onClose.pipe(
          filter(value => !!value),
          tap((priceSheetData: {selectedPriceSheet: KPI.PriceSheet, priceSheetTier: PriceTier}) => {
            const selectedPriceSheet = priceSheetData.selectedPriceSheet;
            console.debug('selected pricesheet', selectedPriceSheet);
            const quantity = this.newAssetState ? this.newAssetState.value.quantity : this.selectedAssetState.quantity;
            let price: number = 0;
            switch (priceSheetData.priceSheetTier) {
              case 'med':
                price = Math.round(Number(selectedPriceSheet.average_price));
                break;
              case 'max':
                price = Math.round(Number(selectedPriceSheet.max_price));
                break;
              case 'min':
                price = Math.round(Number(selectedPriceSheet.min_price));
                break;
              default:
                price = Math.round(Number(selectedPriceSheet.average_price));
                break;
            }
            if (quantity) {
              price = price * quantity
            }

            this.budgetForm.patchValue({ price, price_sheet_id: selectedPriceSheet.id });
            if (budgetOriginPriceSheet) this.budgetForm.patchValue({ budget_origin: budgetOriginPriceSheet });
          }),
        );
      }),
    ).subscribe();
  }

  public anyFieldValueChanged(): boolean {
    if (!this.budgetForm) {
      return false;
    }

    const initialValuesCount = Object.keys(this.initialValues).length;
    if (initialValuesCount === 0) {
      return false;
    }

    const currentValues = this.budgetForm.getRawValue();

    const hasChanges = Object.keys(this.initialValues).some((key: string) => {
      const currentValue = currentValues[key];
      const initialValue = this.initialValues[key];
      const changed = !R.equals(initialValue, currentValue);
      return changed;
    });

    return hasChanges;
  }

  public isFormValid(): boolean {
    if (!this.budgetForm) {
      return false;
    }

    this.budgetForm.updateValueAndValidity({ emitEvent: false });

    const isValid = this.budgetForm.valid;

    return isValid;
  }

  public refreshForm(): void {
    this.initialValues = { ...this.budgetForm.getRawValue() };
  }

  public setReadOnlyMode(readOnly: boolean): void {
    if (!this.budgetForm) {
      return;
    }

    if (readOnly) {
      this.budgetForm.disable({ emitEvent: false });
      if (this.showStatusFormControl()) {
        this.budgetForm.get('status')?.enable({ emitEvent: false });
      }
    } else {
      this.budgetForm.enable({ emitEvent: false });
    }

    this.budgetForm.updateValueAndValidity({ emitEvent: false });
  }
}
